import { attributeFilterSchemaModule } from '@/components/common/filter/filters/AttributeFilterSchemaModule';
import { commonFilterSchemaModule } from '@/components/common/filter/filters/CommonFilterSchemaModule';
import { OptionalIFormLayout, SimpleFilterOptions } from '@/components/common/filter/filters/types';
import { EpisodeTypesMap } from '@/store/application/data.assets';
import { dataAssetsModule } from '@/store/application/data.assets.module';
import { PageTypes } from '@/store/application/page.definitions';
import { configModule } from '@/store/config';
import { IFormLayoutItem } from '@/uikit/forms/NForm.vue';

function getDividerWebhookAdapter(isWebhook = false) {
  const classes = isWebhook ? 'label-m' : 'heading-xl';
  return (options: SimpleFilterOptions) => ({ ...options, classes: `${options.classes} ${classes}` });
}

export function getEventFiltersBuilder(options: { small: boolean; cardType?: string }, isWebhook = false) {
  const getDividerOptions = getDividerWebhookAdapter(isWebhook);
  const availableObjects = dataAssetsModule.availableObjectsTypedMap;
  const commonAll: OptionalIFormLayout[] = [
    commonFilterSchemaModule.getMatchedLists(options),
    isWebhook ? undefined : commonFilterSchemaModule.getAcknowledged({ ...options }),
    commonFilterSchemaModule.getCameraGroups(options),
    commonFilterSchemaModule.getCamerasDependedOnCameraGroups(options),
    commonFilterSchemaModule.getId({ ...options, name: 'cameras', path: 'cameras', dataQa:'cameras_id', valueType: 'number', i18n_label: 'common.cameras_id_in', i18n_placeholder: 'common.cameras_id_in_placeholder' }),
    isWebhook ? undefined : commonFilterSchemaModule.getDateTimeRange(options),
    isWebhook ? undefined : commonFilterSchemaModule.getVideoArchiveId(options),
    commonFilterSchemaModule.getMatchedCardSelect({ ...options }),
  ];
  
  const lines: OptionalIFormLayout[] = [
    commonFilterSchemaModule.getLineId(options),
    commonFilterSchemaModule.getLineCrossDirection(options),
    isWebhook ? commonFilterSchemaModule.getBackwardLineCrossingWebhooks(options) : commonFilterSchemaModule.getBackwardLineCrossing(options)
  ];

  const localStranger: OptionalIFormLayout[] = [
    configModule.features.extended_event_analysis_enabled ? commonFilterSchemaModule.getLocalStranger({ ...options }) : undefined
  ];

  const faceFeatures: OptionalIFormLayout[] = availableObjects.face ? attributeFilterSchemaModule.getFaceAttributes(options, isWebhook) : [];
  const bodyFeatures: OptionalIFormLayout[] = availableObjects.body ? attributeFilterSchemaModule.getBodyAttributes(options, true, isWebhook) : [];
  const carFeatures: OptionalIFormLayout[] = availableObjects.car ? attributeFilterSchemaModule.getCarAttributes(options, isWebhook) : [];

  const commonEvents: OptionalIFormLayout[] = [
    isWebhook ? commonFilterSchemaModule.getDirectMatchWithConfidence({ ...options }) : commonFilterSchemaModule.getMatches({ ...options }),
    ...commonAll,
    isWebhook ? undefined : commonFilterSchemaModule.getEpisodeId(options),
    isWebhook ? undefined : commonFilterSchemaModule.getEventId(options),
    commonFilterSchemaModule.getBsType({ ...options, multiple: isWebhook }),
    ...lines
  ];

  function getFilterByType(pageType: string, objectType: string, episodeType: string): IFormLayoutItem[] {
    let filterSchema: OptionalIFormLayout[] = [];
    if (pageType === PageTypes.Events) {
      switch (objectType) {
        case 'faces':
          filterSchema = [...commonEvents, ...localStranger, commonFilterSchemaModule.getAttributesDivider(getDividerOptions(options)), ...faceFeatures];
          break;
        case 'bodies':
          filterSchema = [...commonEvents, commonFilterSchemaModule.getAttributesDivider(getDividerOptions(options)), ...bodyFeatures];
          break;
        case 'cars':
          filterSchema = [...commonEvents, commonFilterSchemaModule.getAttributesDivider(getDividerOptions(options)), ...carFeatures];
          break;
      }
    }
    if (pageType === PageTypes.Episodes) {
      const facesEnabled = configModule.getBoolean('objects.faces.enabled', false);
      const bodiesEnabled = configModule.getBoolean('objects.bodies.enabled', false);
      switch (episodeType) {
        case EpisodeTypesMap.Humans:
          filterSchema = [
            (isWebhook || !facesEnabled || !bodiesEnabled) ? undefined : commonFilterSchemaModule.getFaceBodyFilter(options),
            isWebhook ? commonFilterSchemaModule.getEpisodeAllowedTypes(options) : undefined,
            facesEnabled
                ? isWebhook
                    ? commonFilterSchemaModule.getFaceDirectMatch({ ...options })
                    : commonFilterSchemaModule.getFaceMatches({ ...options })
                :undefined,
            bodiesEnabled
              ? isWebhook
                ? commonFilterSchemaModule.getBodyDirectMatch({ ...options })
                : commonFilterSchemaModule.getBodyMatches({ ...options })
              : undefined,
            ...commonAll,
            isWebhook ? undefined : commonFilterSchemaModule.getEpisodeIdInEpisodes(options),
            commonFilterSchemaModule.getEventsCount(options)
          ];

          if (!isWebhook) {
            filterSchema = filterSchema.concat([
              commonFilterSchemaModule.getFaceAttributesDivider(getDividerOptions(options)),
              ...faceFeatures.filter((item: any) => item.name !== 'headpose_yaw' && item.name !== 'headpose_pitch'),
              commonFilterSchemaModule.getBodyAttributesDivider(getDividerOptions(options)),
              ...bodyFeatures
            ]);
          }
          break;
        case EpisodeTypesMap.Cars:
          filterSchema = [
            isWebhook ? commonFilterSchemaModule.getEpisodeAllowedTypes(options) : undefined,
            isWebhook ? commonFilterSchemaModule.getCarDirectMatch({ ...options }) : commonFilterSchemaModule.getCarMatches({ ...options }),
            ...commonAll,
            isWebhook ? undefined : commonFilterSchemaModule.getEpisodeIdInEpisodes(options),
            commonFilterSchemaModule.getEventsCount(options)
          ];

          if (!isWebhook) {
            filterSchema = filterSchema.concat([
              commonFilterSchemaModule.getCarAttributesDivider(getDividerOptions(options)),
              ...carFeatures.filter((item: any) => item.path !== 'orientation')
            ]);
          }
          break;
      }
    }

    return filterSchema.filter((v) => !!v) as any;
  }

  return { getFilterByType };
}
